import DropOffForm from '@/src/app/features/accounting/forms/DocumentDropOff/DropOffForm';
import DropOffSuccess from '@/src/app/features/accounting/forms/DocumentDropOff/DropOffSuccess';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingButton from '../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { useCompanyContext } from '../../../context/CompanyContext';
import COSuccess from '../../../features/accounting/forms/ChangeOrders/COSuccess';
import { DocumentDropOffFields } from '../../../Interfaces/Accounting/ACForms.interfaces';
import { postDocumentDropOff } from '../../../services/accounting/ACFormsService';

const DocumentDropOff = () => {
  const { selectedCompany } = useCompanyContext();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');
  const formMethods = useForm<DocumentDropOffFields>();
  const { handleSubmit } = formMethods;
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const { mutate, isLoading, isSuccess, reset } = useMutation({
    mutationFn: (data: DocumentDropOffFields) => {
      return postDocumentDropOff({
        companyID: selectedCompany!.id,
        section: section ?? '',
        ...data,
      });
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Document Drop Off Upload Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: DocumentDropOffFields) => {
    mutate(data);
  };

  useEffect(() => {
    reset();
    formMethods.reset();
  }, [selectedCompany, formMethods, reset]);

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <div className="text-35px font-bold text-center mb-2 relative capitalize">
        {section} Document Drop Off
      </div>
      <FormProvider {...formMethods}>
        {!isSuccess && (
          <form className="max-w-600px w-full mx-auto flex flex-column gap-5">
            <DropOffForm />
            <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
              <ConfirmationDialog
                Button={
                  <LoadingButton
                    isLoading={isLoading}
                    label="Send"
                    className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                    fontSize="text-22px"
                    type="button"
                  />
                }
                onConfirm={handleSubmit(onSubmit)}
                message="Are you sure you want to send the documents?"
              />
              <Button
                onClick={() => {
                  navigate('/accounting-dashboard/new');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3"
                type="button"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
            </div>
          </form>
        )}
        {isSuccess && <DropOffSuccess />}
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default DocumentDropOff;
